<script setup lang="ts">
import type { IMaintenanceReportBySensor } from '@/interfaces/api/maintenance'
import VueApexCharts from 'vue3-apexcharts'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  items: IMaintenanceReportBySensor[]
  chartTitle: string
  seriesName: string
}>()

const { t } = useI18n()

const TOP_SENSOR_LIMIT = 5

const isShowingAll = ref(false)

watch(
  () => props.items,
  () => {
    isShowingAll.value = false
  },
  { deep: true },
)

const sorted = computed(() => [...props.items].sort((a, b) => b.count - a.count))

const displayedSensors = computed(() =>
  isShowingAll.value ? sorted.value : sorted.value.slice(0, TOP_SENSOR_LIMIT),
)

const showSensorRangeToggle = computed(() => props.items.length > TOP_SENSOR_LIMIT)

const categories = computed(() =>
  displayedSensors.value.map((s) => (s.name?.trim() ? s.name : `Sensor ${s.sensor_id}`)),
)

const data = computed(() => displayedSensors.value.map((s) => s.count))

const pixelsPerRow = 40

const chartHeight = computed(() =>
  Math.max(280, displayedSensors.value.length * pixelsPerRow + 80),
)

const valueMax = computed(() => Math.max(...data.value, 1))

const chartAxisMax = computed(() => {
  const m = valueMax.value
  return m + Math.max(0.5, m * 0.12)
})

const BAR_COLOR = '#04304c'
const DATA_LABEL_COLOR = '#000A1E'
const AXIS_LABEL_COLOR = '#75787B'
const GRID_BORDER_COLOR = '#E2E8F0'

const chartOptions = computed(() => ({
  chart: {
    type: 'bar' as const,
    fontFamily: 'Satoshi, sans-serif',
    toolbar: { show: false },
    redrawOnParentResize: true,
    redrawOnWindowResize: true,
  },
  plotOptions: {
    bar: {
      horizontal: true,
      barHeight: '78%',
      dataLabels: {
        position: 'top',
      },
    },
  },
  dataLabels: {
    enabled: true,
    textAnchor: 'start' as const,
    offsetX: 8,
    style: {
      colors: [DATA_LABEL_COLOR],
      fontWeight: 600,
    },
  },
  xaxis: {
    categories: categories.value,
    min: 0,
    max: chartAxisMax.value,
    stepSize: 1,
    decimalsInFloat: 0,
    forceNiceScale: false,
    labels: {
      maxWidth: 180,
      style: {
        colors: AXIS_LABEL_COLOR,
        fontWeight: 500,
      },
      formatter: (val: string) => {
        const n = Number(val)
        return Number.isFinite(n) ? String(Math.round(n)) : val
      },
    },
  },
  colors: [BAR_COLOR],
  grid: {
    borderColor: GRID_BORDER_COLOR,
    padding: {
      right: 44,
    },
  },
  tooltip: {
    y: {
      formatter: (val: number) => `${Math.round(val)}`,
    },
  },
}))

const series = computed(() => [
  {
    name: props.seriesName,
    data: data.value,
  },
])

const chartKey = computed(
  () => `${isShowingAll.value ? 'all' : 'top'}|${categories.value.join('|')}`,
)

function toggleSensorRange() {
  isShowingAll.value = !isShowingAll.value
}
</script>

<template>
  <div v-if="items.length" class="flex h-full min-h-0 w-full min-w-0 max-w-full flex-col gap-8">
    <h2 class="shrink-0 font-satoshi text-title-sm font-bold leading-snug text-black">
      {{ chartTitle }}
    </h2>
    <div
      class="flex min-h-[320px] min-w-0 max-w-full flex-1 flex-col bg-white p-4 shadow-md">
      <VueApexCharts
        :key="chartKey"
        type="bar"
        :height="chartHeight"
        width="100%"
        class="min-w-0 max-w-full shrink-0"
        :options="chartOptions"
        :series="series" />
      <div
        v-if="showSensorRangeToggle"
        class="mt-4 flex shrink-0 justify-center border-t border-stroke pt-4">
        <button
          type="button"
          class="border border-stroke bg-white px-4 py-2 font-satoshi text-title-xsm font-semibold text-black hover:bg-gray-2"
          @click="toggleSensorRange">
          {{
            isShowingAll
              ? t('maintenance_report.body.result.charts.sensor_chart_see_top_five')
              : t('maintenance_report.body.result.charts.sensor_chart_see_all', {
                  count: items.length,
                })
          }}
        </button>
      </div>
    </div>
  </div>
</template>
